import { useTranslation } from 'react-i18next'
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useChartTheme } from '../../hooks/useChartTheme'

const STATUS_STYLE = {
  progressing: 'bg-green-100 text-green-700',
  stalled: 'bg-amber-100 text-amber-700',
  declining: 'bg-red-100 text-red-700',
  plateau_at_equipment_limit: 'bg-blue-100 text-blue-700',
  insufficient_data: 'bg-gray-100 text-gray-700',
}

function progressionLabel(exercise) {
  return exercise.plateau_at_equipment_limit ? 'plateau_at_equipment_limit' : exercise.status
}

/**
 * Merge variant series on one month axis. Each variant gets its own dataKey and is
 * null outside its own months, so with connectNulls=false no line is ever drawn
 * from one variant to the next — the switch shows as a marker, not a drop.
 */
function buildSeries(exercises) {
  const months = new Set()
  exercises.forEach((ex) => ex.monthly.forEach((m) => months.add(m.month)))
  return [...months].sort().map((month) => {
    const row = { month }
    exercises.forEach((ex, i) => {
      const point = ex.monthly.find((m) => m.month === month)
      row[`s${i}`] = point && point.value != null ? Math.round(point.value * 10) / 10 : null
    })
    return row
  })
}

export function StatusBadge({ exercise }) {
  const { t } = useTranslation()
  const label = progressionLabel(exercise)
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[label]}`} data-testid="status-badge">
      {t(`trainingAnalysis.status.${label}`)}
    </span>
  )
}

/** VH-225: estimated 1RM per month for one exercise, or for the linked variants of one movement. */
export default function ExerciseProgressionChart({ title, exercises, switches = [] }) {
  const { t } = useTranslation()
  const chart = useChartTheme()
  const data = buildSeries(exercises)
  const colors = [chart.accent, chart.warning, chart.info, chart.success]
  const metric = exercises[0]?.metric

  return (
    <article className="bg-surface-container border rounded-lg p-4" data-testid="progression-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        <div className="flex flex-wrap gap-1">
          {exercises.map((ex) => (
            <span key={ex.exercise_name} className="flex items-center gap-1 text-xs">
              {exercises.length > 1 && <span className="text-gray-600">{ex.exercise_name}</span>}
              <StatusBadge exercise={ex} />
            </span>
          ))}
        </div>
      </div>
      <p className="text-xs text-gray-600 mb-2">
        {metric === 'reps' ? t('trainingAnalysis.progression.repsMetric', { load: exercises[0].load_kg }) : t('trainingAnalysis.progression.e1rmMetric')}
      </p>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: chart.axis }} />
            <YAxis tick={{ fontSize: 11, fill: chart.axis }} domain={['auto', 'auto']} width={40} />
            <Tooltip {...(chart.tooltipProps || {})} />
            {exercises.length > 1 && <Legend />}
            {exercises.map((ex, i) => (
              <Line key={ex.exercise_name} type="monotone" dataKey={`s${i}`} name={ex.exercise_name}
                stroke={colors[i % colors.length]} connectNulls={false} isAnimationActive={false} />
            ))}
            {switches.filter((s) => s.date).map((s) => (
              <ReferenceLine key={`${s.from_exercise_name}-${s.date}`} x={s.date.slice(0, 7)} stroke={chart.axis}
                strokeDasharray="4 4" label={{ value: t('trainingAnalysis.progression.switch'), fontSize: 10, fill: chart.axis }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {switches.filter((s) => s.date).map((s) => (
        <p key={`n-${s.from_exercise_name}-${s.date}`} className="text-xs text-gray-600 mt-1" data-testid="switch-note">
          {t('trainingAnalysis.progression.switchNote', { from: s.from_exercise_name, to: s.to_exercise_name, date: s.date })}
        </p>
      ))}
      {exercises.map((ex) => ex.plateau_at_equipment_limit && (
        <p key={`p-${ex.exercise_name}`} className="text-xs text-blue-700 mt-1">
          {t('trainingAnalysis.progression.plateau', { name: ex.exercise_name, weight: ex.plateau_at_equipment_limit.weight_kg, weeks: ex.plateau_at_equipment_limit.weeks })}
        </p>
      ))}
      {exercises.map((ex) => ex.load_drop && (
        <p key={`d-${ex.exercise_name}`} className="text-xs text-amber-700 mt-1">
          {t('trainingAnalysis.progression.loadDrop', { name: ex.exercise_name, pct: Math.abs(ex.load_drop.change_pct), from: ex.load_drop.from_month, to: ex.load_drop.to_month })}
        </p>
      ))}
    </article>
  )
}
